import { copyToClipboard } from "../../services/Utils/ClipboardUtil";
import { Share } from '@capacitor/share';
import { t, Trans } from '@lingui/macro';
import { useState, useReducer, useEffect, useRef, useMemo } from "react";
import { Link2, Save, Trash2, Copy, Share2, ChevronUp, AlertTriangle, Info } from 'lucide-react';

import { Room } from "../../models/Room";
import { RoomInvite } from "../../models/RoomInvite";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { useToast } from "../../hooks/use-toast";
import { useAppUIContext } from "../../context/AppUIContext";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { IconChevronDown } from './FigmaIcons';

export const InviteLinkModal: React.FC<{ room: Room }> = ({ room }) => {
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const expireDateInputRef = useRef<HTMLInputElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [invite, setInvite] = useState<RoomInvite>();
  const [originalInvite, setOriginalInvite] = useState<RoomInvite>();
  const { toast } = useToast();
  const { workspaces } = useAppUIContext();
  const workspace = useMemo(() => workspaces.find(w => w.id === room.workspaceId), [workspaces, room.workspaceId]);
  const guestPolicy = workspace?.guestInvitationPolicy ?? 'ALLOW_ALL';
  // const [inProgress, setInProgress] = useState(false); // Used for loading states if needed
  const [isShowUrl, setIsShowUrl] = useState(false);
  const [isPassword, setIsPassword] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [expiredDate, setExpiredDate] = useState<Date>(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ title: string; message: string; onConfirm: () => void }>({
    title: "",
    message: "",
    onConfirm: () => { },
  });

  useEffect(() => {
    if (showModal) {
      onPresent();
    }
  }, [showModal]);

  async function onPresent() {
    // setInProgress(true);
    let curInvite = await Fire.getCurrentInvite(room, My.user);
    if (curInvite.password != "") {
      setIsPassword(true);
    }
    if (curInvite.expireDate) {
      setIsExpired(true);
    }
    setOriginalInvite(curInvite ? { ...curInvite } : undefined);
    setInvite(curInvite);
    // setInProgress(false);
  }

  const expireDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && invite) {
      invite.expireDate = new Date(value);
      setIsExpired(true);
      forceUpdate();
    }
  };

  const deleteLink = () => {
    if (!invite) {
      toast({ title: t`Error`, description: t`Invite is not defined`, variant: "destructive" });
      return;
    }
    setAlertConfig({
      title: t`Delete link`,
      message: t`Are you sure you want to delete this link?`,
      onConfirm: async () => {
        // setInProgress(true);
        Fire.deleteRoomInvite(invite.id).then(() => {
          // setInProgress(false);
          toast({ description: t`Invitation link deleted` });
          setShowModal(false);
          setIsShowUrl(false);
          setInvite(undefined);
          setOriginalInvite(undefined);
        }).catch((error) => {
          // setInProgress(false);
          console.error("Error deleting invite link:", error);
          toast({ title: t`Error`, description: t`Failed to delete invitation link`, variant: "destructive" });
        });
      }
    });
    setAlertOpen(true);
  }

  const isChanged = () => {
    if (!invite) return false;
    if (!originalInvite) return false;

    if (isExpired) {
      if (!invite.expireDate) return true;
    } else if (invite.expireDate) {
      return true;
    }

    if (isPassword) {
      if (!invite.password) return true;
    } else if (invite.password) {
      return true;
    }

    if (invite.password !== originalInvite.password) return true;

    // Date comparison
    const inviteTime = invite.expireDate ? new Date(invite.expireDate).getTime() : 0;
    const originalTime = originalInvite.expireDate ? new Date(originalInvite.expireDate).getTime() : 0;
    if (inviteTime !== originalTime) return true;

    return false;
  }

  const saveChanges = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!invite) {
        toast({ title: t`Error`, description: t`Invite is not defined`, variant: "destructive" });
        reject(new Error('Invite undefined'));
        return;
      }
      // setInProgress(true);
      if (!isExpired) {
        invite.expireDate = undefined;
      } else if (!invite.expireDate) {
        invite.expireDate = expiredDate;
      }

      if (!isPassword) {
        invite.password = '';
      } else {
        if (!invite.password) {
          setIsPassword(false);
        }
      }

      Fire.saveRoomInvite(invite).then(() => {
        // setInProgress(false);
        toast({ description: t`Invitation link saved` });
        setIsShowUrl(true);
        setOriginalInvite(invite ? { ...invite } : undefined);
        resolve();
      }).catch((error) => {
        // setInProgress(false);
        console.error("Error creating invite link:", error);
        toast({ title: t`Error`, description: t`Failed to create invitation link`, variant: "destructive" });
        reject(error);
      });
    });
  }

  const getShareLink = () => {
    if (!invite) return null;

    let url = RoomInvite.getUrl(invite.id);

    return (
      <div className="space-y-2 mb-4">
        <div
          onClick={() => setIsShowUrl(!isShowUrl)}
          className="group bg-primary/20 p-3 rounded-md text-xs sm:text-sm text-card-foreground cursor-pointer border border-primary/20 hover:border-primary/30 transition-all w-full overflow-hidden flex items-center justify-between gap-2"
        >
          <span className={`flex-1 min-w-0 ${!isShowUrl ? 'truncate' : 'break-all whitespace-normal'}`}>
            {url}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <div className="text-muted-foreground group-hover:text-primary transition-colors">
              {isShowUrl ? <ChevronUp className="w-4 h-4" /> : <IconChevronDown className="w-4 h-4" />}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/30"
              onClick={(e) => {
                e.stopPropagation();
                const copyAction = () => copyToClipboard(url).then(() => {
                  toast({ description: t`Copied to clipboard` });
                  setShowModal(false);
                });

                if (isChanged()) {
                  setAlertConfig({
                    title: t`Unsaved changes`,
                    message: t`There are unsaved changes. Save and copy the link?`,
                    onConfirm: () => {
                      saveChanges().then(copyAction).catch(() => { });
                    }
                  });
                  setAlertOpen(true);
                  return;
                }
                copyAction();
              }}
            >
              <Copy className="w-3.5 h-3.5" />
              <Trans>Copy</Trans>
            </Button>
          </div>
        </div>

        {!UtilApp.isMac(window) && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="w-full sm:flex-1 gap-2"
              onClick={async () => {
                try {
                  await Share.share({
                    title: t`Share invitation URL`,
                    url: url
                  });
                  setShowModal(false);
                } catch (e: any) {
                  // 사용자가 취소한 경우 무시 (Sentry에 보내지 않음)
                  if (e?.message?.includes('canceled') || e?.message?.includes('cancelled')) {
                    console.log('Share canceled by user');
                    return;
                  }
                  throw e; // 다른 에러는 다시 throw
                }
              }}
            >
              <Share2 className="w-4 h-4" /> <Trans>Share URL</Trans>
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogTrigger asChild>
          <Button variant="icon-panel" title={t`Invite link`}>
            <Link2 />
          </Button>
        </DialogTrigger>

        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-lg overflow-y-auto max-h-[90vh] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle><Trans>Invite with a link</Trans></DialogTitle>
          </DialogHeader>

          {invite && (
            <div className="space-y-4 py-2 w-full min-w-0 overflow-hidden">
              <div className="w-full min-w-0">{getShareLink()}</div>

              <p className="text-sm text-muted-foreground">
                <Trans>Send this link to people you want to join this channel.</Trans>
              </p>

              {/* Guest 초대 정책 안내 */}
              {guestPolicy === 'DISALLOW_ALL' && (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-xs text-destructive">
                    <Trans>This workspace does not allow guest invitations. Only users with allowed email domains can join.</Trans>
                  </p>
                </div>
              )}
              {guestPolicy === 'RESTRICT_TO_DOMAINS' && workspace?.guestDomains && workspace.guestDomains.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700">
                    <p><Trans>Guest invitations are restricted to the following domains:</Trans></p>
                    <p className="font-medium mt-1">{workspace.guestDomains.join(', ')}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4 border-t pt-4">
                {/* Password */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full min-w-0">
                  <Label htmlFor="password-toggle" className="font-medium shrink-0"><Trans>Password</Trans></Label>
                  <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
                    <Input
                      ref={passwordInputRef}
                      type="text"
                      className="h-8 w-0 flex-1 min-w-0 sm:w-32"
                      value={invite?.password || ""}
                      onChange={e => {
                        if (invite) {
                          invite.password = e.target.value;
                          if (e.target.value) {
                            setIsPassword(true);
                          } else {
                            setIsPassword(false);
                          }
                          forceUpdate();
                        }
                      }}
                      onBlur={() => {
                        if (invite && !invite.password && isPassword) {
                          setIsPassword(false);
                        }
                      }}
                      placeholder={t`Password`}
                    />
                    <Switch
                      id="password-toggle"
                      checked={isPassword}
                      onCheckedChange={(checked) => {
                        setIsPassword(checked);
                        if (!checked && invite) {
                          invite.password = "";
                          forceUpdate();
                        } else if (checked) {
                          setTimeout(() => passwordInputRef.current?.focus(), 0);
                        }
                      }}
                      className="shrink-0"
                    />
                  </div>
                </div>

                {/* Expire Date */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full min-w-0">
                  <Label htmlFor="expire-toggle" className="font-medium shrink-0"><Trans>Expire date</Trans></Label>
                  <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
                    <Input
                      ref={expireDateInputRef}
                      type="date"
                      className="h-8 w-0 flex-1 min-w-0 sm:w-auto sm:min-w-[130px]"
                      value={isExpired ? (invite?.expireDate ? new Date(new Date(invite.expireDate).getTime() + (-new Date().getTimezoneOffset() * 60 * 1000)).toISOString().split('T')[0] : expiredDate.toISOString().split('T')[0]) : ""}
                      onChange={expireDateChange}
                      onBlur={() => {
                        if (!isExpired && invite?.expireDate) {
                          // If not toggled, we keep the UI value but state remains undefined for the invite
                        }
                      }}
                    />
                    <Switch
                      id="expire-toggle"
                      checked={isExpired}
                      onCheckedChange={(checked) => {
                        setIsExpired(checked);
                        if (!checked && invite) {
                          invite.expireDate = undefined;
                          forceUpdate();
                        } else if (checked) {
                          setTimeout(() => expireDateInputRef.current?.focus(), 0);
                        }
                      }}
                      className="shrink-0"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-4">
                <Button
                  className="flex-1 gap-2 bg-primary hover:bg-primary/90 text-on-primary-text"
                  onClick={() => saveChanges()}
                  disabled={!isChanged()}
                >
                  <Save className="w-4 h-4" /> <Trans>Save changes</Trans>
                </Button>
                <Button
                  variant="outline"
                  className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={deleteLink}
                >
                  <Trash2 className="w-4 h-4" /> <Trans>Delete link</Trans>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            <AlertDialogDescription>{alertConfig.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={() => { alertConfig.onConfirm(); setAlertOpen(false); }}>
              <Trans>Confirm</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default InviteLinkModal;